import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Users, CalendarDays, Inbox } from "lucide-react";
import { api, extractErr } from "@/portal/PortalAuth";
import { card, inp, btn, ghost } from "@/portal/PortalShell";

const CATEGORIES = [
  { value: "", label: "All categories" },
  { value: "furnished_private_home", label: "Furnished Private Homes" },
  { value: "luxury_home", label: "Vacation Homes" },
];
const STATUSES = ["New", "Contacted", "Follow-Up Needed", "Closed"];
const BADGE = { New: "bg-[#FEF3C7] text-[#92400E]", Contacted: "bg-[#DBEAFE] text-[#1E40AF]", "Follow-Up Needed": "bg-[#FEF3C7] text-[#92400E]", Closed: "bg-[#F5F5F4] text-[#57534E]" };

export default function Inquiries() {
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (category) params.category = category;
      if (status) params.status = status;
      const { data } = await api.get("/admin/inquiries", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(extractErr(e)); setItems([]); }
  }, [category, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="inquiries-panel" className="space-y-4">
      <div className={card + " flex flex-wrap items-center gap-3 justify-between"}>
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#8A9A86]">Coming-soon inquiries</p>
          <p className="text-sm text-[#44403C] mt-0.5">Interest captured for Furnished Private Homes & Vacation Homes (no booking/payment).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="inq-filter-category" className={inp + " h-9 text-sm"}>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="inq-filter-status" className={inp + " h-9 text-sm"}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {items === null && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#8A9A86]" /></div>}
      {items && items.length === 0 && (
        <div className={card + " text-center py-12"} data-testid="inquiries-empty">
          <Inbox className="w-7 h-7 text-[#D6D3D1] mx-auto" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-[#78716C]">No inquiries yet in this view.</p>
        </div>
      )}
      {items && items.map((q) => <InquiryCard key={q.id} q={q} onChange={load} />)}
    </div>
  );
}

function InquiryCard({ q, onChange }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const dates = [q.check_in, q.check_out].filter(Boolean).join(" → ") || "Not specified";

  const setStatus = async (s) => {
    setBusy(true);
    try { await api.patch(`/admin/inquiries/${q.id}`, { status: s }); toast.success(`Marked ${s}`); onChange(); }
    catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.patch(`/admin/inquiries/${q.id}`, { internal_note: note.trim() }); setNote(""); toast.success("Note added"); onChange(); }
    catch (e) { toast.error(extractErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className={card + " space-y-3"} data-testid={`inquiry-${q.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-[#1C1917]">{q.name} <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-medium ${BADGE[q.status] || "bg-[#F5F5F4]"}`}>{q.status}</span></p>
          <p className="text-xs text-[#8A9A86] mt-0.5">{q.category_label} · {new Date(q.created_at).toLocaleString()}</p>
        </div>
        <select value={q.status} disabled={busy} onChange={(e) => setStatus(e.target.value)} data-testid={`inq-status-${q.id}`} className={inp + " h-9 text-sm max-w-[180px]"}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-[#44403C]">
        <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#8A9A86]" /> <a href={`mailto:${q.email}`} className="underline underline-offset-2">{q.email}</a></span>
        <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#8A9A86]" /> {dates}</span>
        <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#8A9A86]" /> {q.num_guests ? `${q.num_guests} guests` : "Guests not specified"}</span>
      </div>
      {q.message && <p className="text-sm text-[#44403C] bg-[#FAFAF9] rounded-lg px-3 py-2 whitespace-pre-wrap">{q.message}</p>}
      {(q.internal_notes || []).length > 0 && (
        <ul className="space-y-1 border-t border-[#F5F5F4] pt-2" data-testid={`inq-notes-${q.id}`}>
          {q.internal_notes.map((n, i) => <li key={i} className="text-[11px] text-[#78716C]">{new Date(n.at).toLocaleString()} · {n.by}: {n.note}</li>)}
        </ul>
      )}
      <div className="flex gap-2">
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a follow-up note" data-testid={`inq-note-${q.id}`} className={inp + " h-9"} />
        <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`inq-note-save-${q.id}`} className={ghost + " text-xs whitespace-nowrap"}>Add note</button>
      </div>
    </div>
  );
}
